// Money is stored as integer minor units plus an ISO 4217 currency code. Never floats in storage.

const EXPONENTS: Record<string, number> = {
  TND: 3,
  LYD: 3,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JOD: 3,
  IQD: 3,
  XOF: 0,
  XAF: 0,
  JPY: 0,
  KRW: 0,
  CLP: 0,
  VND: 0,
};

export const SUPPORTED_CURRENCIES = [
  "TND",
  "DZD",
  "MAD",
  "LYD",
  "EGP",
  "EUR",
  "USD",
  "XOF",
  "SAR",
  "AED",
  "QAR",
  "CAD",
  "GBP",
] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];

export function currencyExponent(currency: string): number {
  return EXPONENTS[currency.toUpperCase()] ?? 2;
}

/** Converts a decimal major amount (e.g. 120.5) to integer minor units for the currency. */
export function toMinor(major: number, currency: string): number {
  if (!Number.isFinite(major)) throw new Error("Montant invalide");
  const factor = 10 ** currencyExponent(currency);
  return Math.round(major * factor);
}

/** Converts integer minor units to a decimal major amount, for display and provider payloads only. */
export function fromMinor(minor: number, currency: string): number {
  const factor = 10 ** currencyExponent(currency);
  return minor / factor;
}

/** Parses a user typed amount ("120", "120.5", "120,500") into minor units. Returns null when invalid. */
export function parseMajorToMinor(input: string, currency: string): number | null {
  const cleaned = input.replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const exp = currencyExponent(currency);
  const [intPart, fracPart = ""] = cleaned.split(".");
  if (fracPart.length > exp) return null;
  const sign = cleaned.startsWith("-") ? -1 : 1;
  const intDigits = intPart!.replace("-", "");
  const minor = Number(intDigits) * 10 ** exp + Number(fracPart.padEnd(exp, "0") || "0");
  return sign * minor;
}

/** Adds minor amounts safely (they are integers, this guards against accidental floats). */
export function sumMinor(values: Array<number | null | undefined>): number {
  let total = 0;
  for (const v of values) {
    if (typeof v === "number" && Number.isFinite(v)) total += Math.round(v);
  }
  return total;
}

/** Multiplies a minor amount by a quantity or a rate, rounding half away from zero. */
export function multiplyMinor(minor: number, factor: number): number {
  const raw = minor * factor;
  return raw < 0 ? -Math.round(-raw) : Math.round(raw);
}

/**
 * Converts minor units between currencies with a rate expressed as "1 unit of `from` = rate units of `to`".
 * Exponents are taken into account (USD cents to TND millimes).
 */
export function convertMinor(minor: number, from: string, to: string, rate: number): number {
  if (from.toUpperCase() === to.toUpperCase()) return minor;
  const major = fromMinor(minor, from) * rate;
  return toMinor(major, to);
}

export interface MoneyFormatOptions {
  /** Hide decimals (dashboard KPI style: 12,428 TND). */
  compact?: boolean;
  /** Show the currency code after the amount (default true). */
  withCurrency?: boolean;
  /** Prefix with + for positive values. */
  signed?: boolean;
}

/**
 * Formats like the approved design: "12,428 TND" (compact) and "120.00 TND" (default).
 * Uses comma thousands separators and dot decimals.
 */
export function formatMoney(minor: number | null | undefined, currency: string, opts: MoneyFormatOptions = {}): string {
  const { compact = false, withCurrency = true, signed = false } = opts;
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return withCurrency ? `0 ${currency}` : "0";
  const exp = currencyExponent(currency);
  const major = fromMinor(minor, currency);
  const hasSubCents = exp > 2 && Math.round(Math.abs(minor)) % 10 !== 0;
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: compact ? 0 : Math.min(2, exp),
    maximumFractionDigits: compact ? 0 : hasSubCents ? exp : Math.min(2, exp),
  });
  const body = formatter.format(major);
  const sign = signed && major > 0 ? "+" : "";
  return withCurrency ? `${sign}${body} ${currency}` : `${sign}${body}`;
}
